/**
 * HouseholdForm — the public, self-contained one-page household sign-up form used
 * by the QR /join flow. It mirrors the field set + payload shape of the admin
 * HouseholdDialog but, being public, omits the admin-only fields (member number,
 * membership tier) and renders page-style (no Dialog chrome) with its own submit.
 *
 * It is intentionally separate from HouseholdDialog so the live admin household
 * flow is never at risk; a future refactor could unify the two on a shared core.
 *
 * It only BUILDS the {primary, partner?, dependents? | family?} payload and hands
 * it back via onSubmit — the parent owns the API call + what happens on success
 * (the /join page logs the new primary in).
 */
import React, { useState } from 'react';
import {
  TextField, Button, Stack, Box, Typography, Divider, IconButton,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Alert, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

export type HouseholdType = 'member' | 'non-member';

interface Dependent {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  relationship: string;
}

interface Props {
  type: HouseholdType;
  submitting: boolean;
  serverError?: string | null;
  submitLabel?: string;
  onSubmit: (household: any) => void;
}

const MEMBER_DEP_RELATIONSHIPS = ['CHILD', 'SIBLING', 'PARENT', 'OTHER'];
const NON_MEMBER_RELATIONSHIPS = ['CHILD', 'SPOUSE', 'PARTNER', 'PARENT', 'SIBLING', 'OTHER'];

const emptyDependent = (): Dependent => ({ first_name: '', last_name: '', date_of_birth: '', relationship: 'CHILD' });
const emptyPrimary = {
  first_name: '', last_name: '', email: '', password: '', mobile_phone: '',
  date_of_birth: '', address_line1: '', city: '', state: '', postal_code: '',
  member_number: '',
};
const emptyPartner = {
  first_name: '', last_name: '', email: '', password: '', mobile_phone: '',
  date_of_birth: '', relationship: 'SPOUSE', member_number: '',
};
const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

const HouseholdForm: React.FC<Props> = ({ type, submitting, serverError, submitLabel, onSubmit }) => {
  const isMember = type === 'member';
  const [error, setError] = useState<string | null>(null);
  const [primary, setPrimary] = useState({ ...emptyPrimary });
  const [partnerEnabled, setPartnerEnabled] = useState(false);
  const [partner, setPartner] = useState({ ...emptyPartner });
  const [dependents, setDependents] = useState<Dependent[]>([]);

  const addDependent = () => setDependents((d) => [...d, emptyDependent()]);
  const updateDependent = (i: number, patch: Partial<Dependent>) =>
    setDependents((d) => d.map((dep, idx) => (idx === i ? { ...dep, ...patch } : dep)));
  const removeDependent = (i: number) => setDependents((d) => d.filter((_, idx) => idx !== i));

  const validate = (): string | null => {
    if (!primary.first_name.trim() || !primary.last_name.trim()) return 'Your first and last name are required.';
    if (!primary.email.trim() || !primary.password) return 'An email and password are required (for your login).';
    if (isMember && partnerEnabled) {
      if (!partner.first_name.trim() || !partner.last_name.trim()) return 'Partner first and last name are required.';
      if (!partner.email.trim() || !partner.password) return 'A partner gets their own login, so they need an email and password.';
    }
    for (let i = 0; i < dependents.length; i++) {
      const dep = dependents[i];
      const started = dep.first_name.trim() || dep.last_name.trim() || dep.date_of_birth;
      if (started && (!dep.first_name.trim() || !dep.last_name.trim())) {
        return `${isMember ? 'Child' : 'Family member'} ${i + 1}: first and last name are required.`;
      }
    }
    return null;
  };

  const handleSubmit = () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);

    const filled = dependents.filter((d) => d.first_name.trim() && d.last_name.trim());
    const primaryCommon = {
      first_name: primary.first_name.trim(),
      last_name: primary.last_name.trim(),
      email: primary.email.trim(),
      password: primary.password,
      mobile_phone: primary.mobile_phone.trim() || undefined,
      date_of_birth: primary.date_of_birth || undefined,
      address_line1: primary.address_line1.trim() || undefined,
      city: primary.city.trim() || undefined,
      state: primary.state.trim() || undefined,
      postal_code: primary.postal_code.trim() || undefined,
      // members enter their own club account number; non-members never set it (the
      // server auto-generates theirs, ignoring any supplied value).
      member_number: primary.member_number.trim() || undefined,
    };

    let household: any;
    if (isMember) {
      household = {
        primary: { ...primaryCommon },
        partner: partnerEnabled
          ? {
              first_name: partner.first_name.trim(),
              last_name: partner.last_name.trim(),
              email: partner.email.trim(),
              password: partner.password,
              mobile_phone: partner.mobile_phone.trim() || undefined,
              date_of_birth: partner.date_of_birth || undefined,
              relationship_to_primary: partner.relationship,
              member_number: partner.member_number.trim() || undefined,
            }
          : null,
        dependents: filled.map((d) => ({
          first_name: d.first_name.trim(),
          last_name: d.last_name.trim(),
          date_of_birth: d.date_of_birth || undefined,
          relationship_to_primary: d.relationship || 'CHILD',
          // Kids' account numbers are ALWAYS auto-assigned server-side
          // ({primary}-DPnn) — members never enter them (they wouldn't know the value
          // and would otherwise save something wrong). Staff can override via the desk.
        })),
      };
    } else {
      household = {
        primary: { ...primaryCommon, acquisition_source: 'qr_in_person' },
        family: filled.map((d) => ({
          first_name: d.first_name.trim(),
          last_name: d.last_name.trim(),
          date_of_birth: d.date_of_birth || undefined,
          relationship_type: d.relationship || 'CHILD',
        })),
      };
    }
    onSubmit(household);
  };

  const relationshipOptions = isMember ? MEMBER_DEP_RELATIONSHIPS : NON_MEMBER_RELATIONSHIPS;

  return (
    <Stack spacing={2}>
      {(error || serverError) && (
        <Alert severity="error" onClose={() => setError(null)}>{error || serverError}</Alert>
      )}

      {/* Primary */}
      <Divider textAlign="left"><Typography variant="overline">Your details</Typography></Divider>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField sx={{ flex: 1, minWidth: 140 }} required label="First Name" value={primary.first_name}
          onChange={(e) => setPrimary({ ...primary, first_name: e.target.value })} />
        <TextField sx={{ flex: 1, minWidth: 140 }} required label="Last Name" value={primary.last_name}
          onChange={(e) => setPrimary({ ...primary, last_name: e.target.value })} />
      </Box>
      {isMember && (
        <TextField fullWidth label="Club Account Number" value={primary.member_number}
          onChange={(e) => setPrimary({ ...primary, member_number: e.target.value })}
          placeholder="e.g. AR003"
          helperText="Your club-assigned member number — enter the one you already have." />
      )}
      <TextField fullWidth required type="email" label="Email" value={primary.email}
        onChange={(e) => setPrimary({ ...primary, email: e.target.value })} />
      <TextField fullWidth required type="password" label="Password" value={primary.password}
        onChange={(e) => setPrimary({ ...primary, password: e.target.value })}
        helperText="At least 8 characters, with an upper- and lower-case letter and a number." />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField sx={{ flex: 1, minWidth: 140 }} label="Mobile Phone" value={primary.mobile_phone}
          onChange={(e) => setPrimary({ ...primary, mobile_phone: e.target.value })} />
        <TextField sx={{ flex: 1, minWidth: 140 }} type="date" label="Date of Birth" InputLabelProps={{ shrink: true }}
          value={primary.date_of_birth} onChange={(e) => setPrimary({ ...primary, date_of_birth: e.target.value })} />
      </Box>
      <TextField fullWidth label="Address" value={primary.address_line1}
        onChange={(e) => setPrimary({ ...primary, address_line1: e.target.value })} />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField sx={{ flex: 1, minWidth: 120 }} label="City" value={primary.city}
          onChange={(e) => setPrimary({ ...primary, city: e.target.value })} />
        <TextField sx={{ width: 100 }} label="State" value={primary.state}
          onChange={(e) => setPrimary({ ...primary, state: e.target.value })} />
        <TextField sx={{ width: 140 }} label="Postal Code" value={primary.postal_code}
          onChange={(e) => setPrimary({ ...primary, postal_code: e.target.value })} />
      </Box>

      {/* Partner (members only — a SECONDARY member gets their own login) */}
      {isMember && (
        <>
          <Divider textAlign="left"><Typography variant="overline">Partner / spouse</Typography></Divider>
          <FormControlLabel
            control={<Switch checked={partnerEnabled} onChange={(e) => setPartnerEnabled(e.target.checked)} />}
            label="Add a partner / spouse (gets their own login)" />
          {partnerEnabled && (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField sx={{ flex: 1, minWidth: 140 }} required label="First Name" value={partner.first_name}
                  onChange={(e) => setPartner({ ...partner, first_name: e.target.value })} />
                <TextField sx={{ flex: 1, minWidth: 140 }} required label="Last Name" value={partner.last_name}
                  onChange={(e) => setPartner({ ...partner, last_name: e.target.value })} />
              </Box>
              <TextField fullWidth label="Partner's Club Account Number" value={partner.member_number}
                onChange={(e) => setPartner({ ...partner, member_number: e.target.value })}
                placeholder="e.g. AR004"
                helperText="The partner's own club-assigned number." />
              <TextField fullWidth required type="email" label="Email" value={partner.email}
                onChange={(e) => setPartner({ ...partner, email: e.target.value })} />
              <TextField fullWidth required type="password" label="Password" value={partner.password}
                onChange={(e) => setPartner({ ...partner, password: e.target.value })} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField sx={{ flex: 1, minWidth: 140 }} label="Mobile Phone" value={partner.mobile_phone}
                  onChange={(e) => setPartner({ ...partner, mobile_phone: e.target.value })} />
                <FormControl sx={{ minWidth: 130 }}>
                  <InputLabel>Relationship</InputLabel>
                  <Select label="Relationship" value={partner.relationship}
                    onChange={(e) => setPartner({ ...partner, relationship: e.target.value })}>
                    <MenuItem value="SPOUSE">Spouse</MenuItem>
                    <MenuItem value="PARTNER">Partner</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </>
      )}

      {/* Dependents / family */}
      <Divider textAlign="left">
        <Typography variant="overline">{isMember ? 'Children' : 'Family members'}</Typography>
      </Divider>
      {dependents.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No {isMember ? 'children' : 'family members'} added yet.
        </Typography>
      )}
      {dependents.map((dep, i) => (
        <Box key={i} sx={{
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1,
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          border: { xs: '1px solid', sm: 'none' }, borderColor: 'divider', borderRadius: 1, p: { xs: 1.5, sm: 0 },
        }}>
          <TextField fullWidth size="small" label="First Name" value={dep.first_name}
            onChange={(e) => updateDependent(i, { first_name: e.target.value })} />
          <TextField fullWidth size="small" label="Last Name" value={dep.last_name}
            onChange={(e) => updateDependent(i, { last_name: e.target.value })} />
          <TextField size="small" type="date" label="DOB" InputLabelProps={{ shrink: true }} value={dep.date_of_birth}
            onChange={(e) => updateDependent(i, { date_of_birth: e.target.value })} sx={{ minWidth: 150 }} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Relationship</InputLabel>
            <Select label="Relationship" value={dep.relationship}
              onChange={(e) => updateDependent(i, { relationship: e.target.value })}>
              {relationshipOptions.map((r) => (<MenuItem key={r} value={r}>{titleCase(r)}</MenuItem>))}
            </Select>
          </FormControl>
          <Tooltip title="Remove">
            <IconButton color="error" onClick={() => removeDependent(i)}
              sx={{ alignSelf: { xs: 'flex-end', sm: 'flex-start' }, mt: { sm: 0.5 } }}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <Button startIcon={<AddIcon />} onClick={addDependent} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
        Add {isMember ? 'child' : 'family member'}
      </Button>

      <Button onClick={handleSubmit} variant="contained" size="large" disabled={submitting} sx={{ mt: 1 }}>
        {submitting ? 'Submitting…' : (submitLabel || 'Complete registration')}
      </Button>
    </Stack>
  );
};

export default HouseholdForm;
